import { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { reportImportErrors } from "@/lib/importUtils";
import { toast } from "sonner";
import { Plus, Trash2, Pencil, Save, Upload } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const RESTRICTABLE_PAGES = [
  ["dashboard", "Fatigue Board / Crew Readiness Board"],
  ["tasks", "Tasks"],
  ["task_history", "Task History"],
  ["history", "Fatigue History"],
  ["monthly", "Monthly View"],
];
const PAGE_LABELS = Object.fromEntries(RESTRICTABLE_PAGES);
const DEVICE_CLASSES = [["mobile", "Mobile"], ["tablet", "Tablet"], ["desktop", "Desktop"]];

export default function OrganizationTab() {
  const { user } = useAuth();
  const [companies, setCompanies] = useState([]);
  const [companyId, setCompanyId] = useState("");
  const [tree, setTree] = useState({ units: [], transfer_team: null });
  const [terms, setTerms] = useState({ unit_label: "Unit", subunit_label: "Subunit", team_label: "Team" });
  const [termDraft, setTermDraft] = useState(terms);
  const [rules, setRules] = useState([]);
  const [roles, setRoles] = useState([]);
  const [posLabel, setPosLabel] = useState("Position");
  const [posOptions, setPosOptions] = useState([]);
  const [posImportOpen, setPosImportOpen] = useState(false);
  const [posImportText, setPosImportText] = useState("");
  const [posImportMode, setPosImportMode] = useState("append");
  const [posBusy, setPosBusy] = useState(false);
  const [draft, setDraft] = useState({ module: "tasks", role_ids: [], unit_ids: [], subunit_ids: [], team_ids: [], devices: ["mobile", "tablet", "desktop"] });

  useEffect(() => {
    const c = companies.find((x) => x.id === companyId);
    setRules(c?.page_restrictions || []);
    if (companyId) api.get(`/roles?company_id=${companyId}`).then((r) => setRoles(r.data)).catch(() => {});
    if (companyId) api.get(`/position-config?company_id=${companyId}`)
      .then((r) => { setPosLabel(r.data.label || "Position"); setPosOptions(r.data.options || []); }).catch(() => {});
  }, [companies, companyId]);

  const saveRules = async (next, msg = "Page restrictions saved") => {
    try {
      const { data } = await api.put(`/admin/companies/${companyId}/page-restrictions`, { rules: next });
      setRules(data.page_restrictions);
      setCompanies((cs) => cs.map((c) => (c.id === companyId ? { ...c, page_restrictions: data.page_restrictions } : c)));
      toast.success(msg);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const toggleDraft = (key, id) => setDraft((d) => ({
    ...d, [key]: d[key].includes(id) ? d[key].filter((x) => x !== id) : [...d[key], id],
  }));

  const addRule = () => saveRules([...rules, draft], "Restriction added");

  useEffect(() => {
    api.get("/companies").then((r) => {
      setCompanies(r.data);
      if (r.data[0]) setCompanyId((prev) => prev || r.data[0].id);
    });
  }, []);

  const load = () => {
    if (!companyId) return;
    api.get(`/org/tree?company_id=${companyId}`).then((r) => {
      setTree({ units: r.data.units, transfer_team: r.data.transfer_team });
      setTerms(r.data.terminology);
      setTermDraft(r.data.terminology);
    }).catch((e) => toast.error(formatApiErrorDetail(e.response?.data?.detail)));
  };
  useEffect(() => { load(); }, [companyId]); // eslint-disable-line react-hooks/exhaustive-deps

  const saveTerms = async () => {
    try {
      await api.put(`/admin/companies/${companyId}/terminology`, termDraft);
      toast.success("Terminology saved");
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const updPos = (i, k, v) => setPosOptions((os) => os.map((o, j) => (j === i ? { ...o, [k]: v } : o)));

  const savePositions = async () => {
    setPosBusy(true);
    try {
      const { data } = await api.put(`/admin/companies/${companyId}/position-config`, {
        label: posLabel, options: posOptions.map((o) => ({ name: o.name, value: Number(o.value) || 0 })),
      });
      setPosLabel(data.label);
      setPosOptions(data.options);
      toast.success("Positions saved");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setPosBusy(false); }
  };

  const runPosImport = async () => {
    if (!posImportText.trim()) { toast.error("Paste one position per line: Name, Level"); return; }
    setPosBusy(true);
    try {
      const { data } = await api.post(`/admin/companies/${companyId}/position-config/import`, { mode: posImportMode, text: posImportText });
      toast.success(`Imported ${data.added} position(s) — ${data.total} total`);
      reportImportErrors(data, "positions", user);
      setPosImportOpen(false);
      setPosImportText("");
      const r = await api.get(`/position-config?company_id=${companyId}`);
      setPosLabel(r.data.label || "Position");
      setPosOptions(r.data.options || []);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setPosBusy(false); }
  };

  const act = async (fn, msg) => {
    try { await fn(); if (msg) toast.success(msg); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const addUnit = () => {
    const n = window.prompt(`${terms.unit_label} name?`);
    if (n) act(() => api.post("/admin/units", { company_id: companyId, name: n }), `${terms.unit_label} added`);
  };
  const renameUnit = (id) => {
    const n = window.prompt(`Rename ${terms.unit_label}:`);
    if (n) act(() => api.put(`/admin/units/${id}`, { name: n }));
  };
  const delUnit = (id) => {
    if (window.confirm(`Delete this ${terms.unit_label}? Members move to the Transfer ${terms.team_label}.`))
      act(() => api.delete(`/admin/units/${id}`));
  };
  const addSubunit = (unitId) => {
    const n = window.prompt(`${terms.subunit_label} name?`);
    if (n) act(() => api.post("/admin/subunits", { company_id: companyId, unit_id: unitId, name: n }), `${terms.subunit_label} added`);
  };
  const renameSubunit = (id) => {
    const n = window.prompt(`Rename ${terms.subunit_label}:`);
    if (n) act(() => api.put(`/admin/subunits/${id}`, { name: n }));
  };
  const delSubunit = (id) => {
    if (window.confirm(`Delete this ${terms.subunit_label}? Members move to the Transfer ${terms.team_label}.`))
      act(() => api.delete(`/admin/subunits/${id}`));
  };
  const addTeam = (subunitId) => {
    const n = window.prompt(`${terms.team_label} name?`);
    if (n) act(() => api.post("/admin/teams", { company_id: companyId, subunit_id: subunitId, name: n }), `${terms.team_label} added`);
  };
  const renameTeam = (id) => {
    const n = window.prompt(`Rename ${terms.team_label}:`);
    if (n) act(() => api.put(`/admin/teams/${id}`, { name: n }));
  };
  const delTeam = (id) => {
    if (window.confirm(`Delete this ${terms.team_label}? Members move to the Transfer ${terms.team_label}.`))
      act(() => api.delete(`/admin/teams/${id}`));
  };

  return (
    <div className="space-y-5">
      <div className="grid grid-cols-1 md:grid-cols-4 gap-3 p-5 rounded-lg border border-slate-800 bg-slate-900/60">
        <div>
          <label className="block text-[10px] uppercase tracking-widest text-slate-500 font-mono-num mb-1.5">Company</label>
          <select value={companyId} onChange={(e) => setCompanyId(e.target.value)} data-testid="org-company-select"
            className="w-full px-3 py-2.5 rounded-md bg-slate-950 border border-slate-800 text-sm">
            {companies.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
        </div>
        {[["unit_label", "Level 1 name"], ["subunit_label", "Level 2 name"], ["team_label", "Level 3 name"]].map(([k, lbl]) => (
          <div key={k}>
            <label className="block text-[10px] uppercase tracking-widest text-slate-500 font-mono-num mb-1.5">{lbl}</label>
            <input value={termDraft[k]} onChange={(e) => setTermDraft({ ...termDraft, [k]: e.target.value })}
              data-testid={`org-term-${k}`}
              className="w-full px-3 py-2.5 rounded-md bg-slate-950 border border-slate-800 text-sm" />
          </div>
        ))}
        <div className="md:col-span-4">
          <button onClick={saveTerms} data-testid="org-save-terms-btn"
            className="inline-flex items-center gap-2 px-3 py-2 rounded-md bg-slate-800 hover:bg-slate-700 text-sm">
            <Save className="h-4 w-4" /> Save level names
          </button>
        </div>
      </div>

      <div className="p-5 rounded-lg border border-slate-800 bg-slate-900/60" data-testid="page-restrictions-card">
        <div className="font-heading text-lg font-semibold mb-1">Page Restrictions</div>
        <p className="text-xs text-slate-500 mb-3">
          Hide pages for selected members — by role, by org level, and per device class.
          The Fatigue survey and Profile are always available; admins always see the full menu.
        </p>
        {rules.length === 0 && <div className="text-xs text-slate-500 mb-3">No restrictions — members see all pages on all devices.</div>}
        <div className="space-y-2 mb-4">
          {rules.map((r) => {
            const orgNames = [
              ...tree.units.filter((u) => (r.unit_ids || []).includes(u.id)).map((u) => `${terms.unit_label}: ${u.name}`),
              ...tree.units.flatMap((u) => (u.subunits || [])).filter((s) => (r.subunit_ids || []).includes(s.id)).map((s) => `${terms.subunit_label}: ${s.name}`),
              ...tree.units.flatMap((u) => (u.subunits || [])).flatMap((s) => (s.teams || [])).filter((t) => (r.team_ids || []).includes(t.id)).map((t) => `${terms.team_label}: ${t.name}`),
            ];
            const roleNames = (r.role_ids || []).map((id) => roles.find((x) => x.id === id)?.name || "?");
            return (
              <div key={r.id} className="flex items-center justify-between gap-3 px-3 py-2 rounded-md border border-slate-800 bg-slate-950/40" data-testid={`page-rule-${r.id}`}>
                <div>
                  <span className="text-sm font-medium block">{PAGE_LABELS[r.module] || r.module}</span>
                  <span className="text-xs text-slate-500">
                    Roles: {roleNames.length ? roleNames.join(", ") : "All"}
                    {" · "}Org: {orgNames.length ? orgNames.join(", ") : "All"}
                    {" · "}Hidden on: {(r.devices || []).join(", ") || "—"}
                  </span>
                </div>
                <button onClick={() => saveRules(rules.filter((x) => x.id !== r.id), "Restriction removed")} data-testid={`page-rule-del-${r.id}`}
                  className="text-rose-400 hover:text-rose-300 shrink-0"><Trash2 className="h-3.5 w-3.5" /></button>
              </div>
            );
          })}
        </div>
        <div className="rounded-md border border-dashed border-slate-700 p-3 space-y-3" data-testid="page-rule-form">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <label className="block text-[10px] uppercase tracking-widest text-slate-500 font-mono-num mb-1.5">Page</label>
              <select value={draft.module} onChange={(e) => setDraft({ ...draft, module: e.target.value })} data-testid="page-rule-module"
                className="w-full px-3 py-2.5 rounded-md bg-slate-950 border border-slate-800 text-sm">
                {RESTRICTABLE_PAGES.map(([k, lbl]) => <option key={k} value={k}>{lbl}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-[10px] uppercase tracking-widest text-slate-500 font-mono-num mb-1.5">Hide on devices</label>
              <div className="flex gap-4 pt-2.5">
                {DEVICE_CLASSES.map(([k, lbl]) => (
                  <label key={k} className="flex items-center gap-2 text-sm cursor-pointer">
                    <input type="checkbox" className="accent-blue-600 h-4 w-4" checked={draft.devices.includes(k)}
                      data-testid={`page-rule-device-${k}`} onChange={() => toggleDraft("devices", k)} />
                    {lbl}
                  </label>
                ))}
              </div>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <label className="block text-[10px] uppercase tracking-widest text-slate-500 font-mono-num mb-1.5">Roles (none ticked = all roles)</label>
              <div className="max-h-28 overflow-y-auto rounded-md border border-slate-800 bg-slate-950 p-2 space-y-1">
                {roles.map((r) => (
                  <label key={r.id} className="flex items-center gap-2 text-xs cursor-pointer">
                    <input type="checkbox" className="accent-blue-600 h-3.5 w-3.5" checked={draft.role_ids.includes(r.id)}
                      data-testid={`page-rule-role-${r.id}`} onChange={() => toggleDraft("role_ids", r.id)} />
                    {r.name}
                  </label>
                ))}
              </div>
            </div>
            <div>
              <label className="block text-[10px] uppercase tracking-widest text-slate-500 font-mono-num mb-1.5">Org (none ticked = whole company)</label>
              <div className="max-h-28 overflow-y-auto rounded-md border border-slate-800 bg-slate-950 p-2 space-y-1">
                {tree.units.map((u) => (
                  <label key={u.id} className="flex items-center gap-2 text-xs cursor-pointer">
                    <input type="checkbox" className="accent-blue-600 h-3.5 w-3.5" checked={draft.unit_ids.includes(u.id)}
                      data-testid={`page-rule-unit-${u.id}`} onChange={() => toggleDraft("unit_ids", u.id)} />
                    {terms.unit_label}: {u.name}
                  </label>
                ))}
                {tree.units.flatMap((u) => (u.subunits || []).map((s) => (
                  <label key={s.id} className="flex items-center gap-2 text-xs cursor-pointer pl-4">
                    <input type="checkbox" className="accent-blue-600 h-3.5 w-3.5" checked={draft.subunit_ids.includes(s.id)}
                      data-testid={`page-rule-subunit-${s.id}`} onChange={() => toggleDraft("subunit_ids", s.id)} />
                    {terms.subunit_label}: {s.name}
                  </label>
                )))}
                {tree.units.flatMap((u) => (u.subunits || []).flatMap((s) => (s.teams || []).map((t) => (
                  <label key={t.id} className="flex items-center gap-2 text-xs cursor-pointer pl-8">
                    <input type="checkbox" className="accent-blue-600 h-3.5 w-3.5" checked={draft.team_ids.includes(t.id)}
                      data-testid={`page-rule-team-${t.id}`} onChange={() => toggleDraft("team_ids", t.id)} />
                    {terms.team_label}: {t.name}
                  </label>
                ))))}
              </div>
            </div>
          </div>
          <button onClick={addRule} data-testid="page-rule-add"
            className="inline-flex items-center gap-2 px-3 py-2 rounded-md bg-blue-600 hover:bg-blue-500 text-white text-sm">
            <Plus className="h-4 w-4" /> Add restriction
          </button>
        </div>
      </div>

      <div className="p-5 rounded-lg border border-slate-800 bg-slate-900/60" data-testid="position-config-card">
        <div className="flex items-center justify-between mb-1">
          <div className="font-heading text-lg font-semibold">{posLabel} structure (name &amp; level)</div>
          <button onClick={() => setPosImportOpen(true)} data-testid="position-import-open"
            className="inline-flex items-center gap-1.5 px-3 py-2 rounded-md bg-slate-800 hover:bg-slate-700 text-xs">
            <Upload className="h-3.5 w-3.5" /> Import
          </button>
        </div>
        <p className="text-xs text-slate-500 mb-4">
          Configurable position/rank list with a numeric level — higher level = more senior. Assign positions to members in the Members tab.
        </p>
        <div className="mb-3 max-w-xs">
          <label className="block text-[10px] uppercase tracking-widest text-slate-500 font-mono-num mb-1.5">Label (e.g. Position, Rank)</label>
          <input value={posLabel} onChange={(e) => setPosLabel(e.target.value)} data-testid="position-label"
            className="w-full px-3 py-2.5 rounded-md bg-slate-950 border border-slate-800 text-sm" />
        </div>
        <div className="space-y-2 mb-3">
          {posOptions.length === 0 && <div className="text-xs text-slate-500">No positions yet — add rows below or import a list (e.g. &quot;MajGen, 19&quot; … &quot;CONT, 0&quot;).</div>}
          {posOptions.map((o, i) => (
            <div key={i} className="flex items-center gap-2" data-testid={`position-row-${i}`}>
              <input value={o.name} onChange={(e) => updPos(i, "name", e.target.value)} data-testid={`position-name-${i}`}
                placeholder="Name (e.g. MAJ)" className="flex-1 px-3 py-2 rounded-md bg-slate-950 border border-slate-800 text-sm" />
              <input type="number" value={o.value} onChange={(e) => updPos(i, "value", e.target.value)} data-testid={`position-value-${i}`}
                placeholder="Level" className="w-24 px-3 py-2 rounded-md bg-slate-950 border border-slate-800 text-sm" />
              <button onClick={() => setPosOptions((os) => os.filter((_, j) => j !== i))} data-testid={`position-del-${i}`}
                className="p-2 rounded-md text-rose-400 hover:bg-rose-900/30 hover:text-rose-300 transition-colors"><Trash2 className="h-3.5 w-3.5" /></button>
            </div>
          ))}
        </div>
        <div className="flex gap-2">
          <button onClick={() => setPosOptions((os) => [...os, { name: "", value: 0 }])} data-testid="position-add"
            className="inline-flex items-center gap-1.5 px-3 py-2 rounded-md bg-slate-800 hover:bg-slate-700 text-xs">
            <Plus className="h-3.5 w-3.5" /> Add position
          </button>
          <button onClick={savePositions} disabled={posBusy} data-testid="position-save"
            className="inline-flex items-center gap-1.5 px-3 py-2 rounded-md bg-blue-600 hover:bg-blue-500 text-white text-xs disabled:opacity-50">
            <Save className="h-3.5 w-3.5" /> Save positions
          </button>
        </div>
      </div>

      <div className="p-5 rounded-lg border border-slate-800 bg-slate-900/60" data-testid="org-tree">
        <div className="flex items-center justify-between mb-4">
          <div className="font-heading text-lg font-semibold">Structure</div>
          <button onClick={addUnit} data-testid="org-add-unit-btn"
            className="inline-flex items-center gap-2 px-3 py-2 rounded-md bg-blue-600 hover:bg-blue-500 text-white text-sm">
            <Plus className="h-4 w-4" /> Add {terms.unit_label}
          </button>
        </div>
        {tree.units.length === 0 && <div className="text-sm text-slate-500">No {terms.unit_label.toLowerCase()}s yet.</div>}
        <div className="space-y-3">
          {tree.units.map((u) => (
            <div key={u.id} className="rounded-md border border-slate-800 bg-slate-950/40" data-testid={`org-unit-${u.id}`}>
              <div className="flex items-center justify-between px-4 py-3 border-b border-slate-800">
                <div className="text-sm font-semibold">
                  <span className="text-[10px] uppercase tracking-widest text-slate-500 font-mono-num mr-2">{terms.unit_label}</span>
                  {u.name}
                </div>
                <div className="flex items-center gap-3">
                  <button onClick={() => addSubunit(u.id)} data-testid={`org-add-subunit-${u.id}`}
                    className="text-xs text-blue-400 hover:text-blue-300 inline-flex items-center gap-1"><Plus className="h-3 w-3" /> {terms.subunit_label}</button>
                  <button onClick={() => renameUnit(u.id)} className="text-xs text-slate-400 hover:text-white"><Pencil className="h-3.5 w-3.5" /></button>
                  <button onClick={() => delUnit(u.id)} className="text-xs text-rose-400 hover:text-rose-300"><Trash2 className="h-3.5 w-3.5" /></button>
                </div>
              </div>
              <div className="p-3 space-y-2">
                {(u.subunits || []).map((s) => (
                  <div key={s.id} className="rounded-md border border-slate-800/70 bg-slate-900/40" data-testid={`org-subunit-${s.id}`}>
                    <div className="flex items-center justify-between px-3 py-2 border-b border-slate-800/70">
                      <div className="text-sm">
                        <span className="text-[10px] uppercase tracking-widest text-slate-500 font-mono-num mr-2">{terms.subunit_label}</span>
                        {s.name}
                      </div>
                      <div className="flex items-center gap-3">
                        <button onClick={() => addTeam(s.id)} data-testid={`org-add-team-${s.id}`}
                          className="text-xs text-blue-400 hover:text-blue-300 inline-flex items-center gap-1"><Plus className="h-3 w-3" /> {terms.team_label}</button>
                        <button onClick={() => renameSubunit(s.id)} className="text-xs text-slate-400 hover:text-white"><Pencil className="h-3.5 w-3.5" /></button>
                        <button onClick={() => delSubunit(s.id)} className="text-xs text-rose-400 hover:text-rose-300"><Trash2 className="h-3.5 w-3.5" /></button>
                      </div>
                    </div>
                    <div className="p-2.5 flex flex-wrap gap-2">
                      {(s.teams || []).length === 0 && <span className="text-xs text-slate-600">No {terms.team_label.toLowerCase()}s.</span>}
                      {(s.teams || []).map((t) => (
                        <div key={t.id} className="inline-flex items-center gap-2 px-2.5 py-1.5 rounded bg-slate-800/70 text-xs" data-testid={`org-team-${t.id}`}>
                          <span className="text-slate-300">{t.name}</span>
                          <button onClick={() => renameTeam(t.id)} className="text-slate-500 hover:text-white"><Pencil className="h-3 w-3" /></button>
                          <button onClick={() => delTeam(t.id)} className="text-rose-500/80 hover:text-rose-300"><Trash2 className="h-3 w-3" /></button>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
        {tree.transfer_team && (
          <div className="mt-4 px-4 py-3 rounded-md border border-dashed border-slate-700 bg-slate-900/40 flex items-center justify-between" data-testid="org-transfer-team">
            <div className="text-sm">
              <span className="text-[10px] uppercase tracking-widest text-slate-500 font-mono-num mr-2">Special</span>
              {tree.transfer_team.name}
            </div>
            <div className="text-xs text-slate-500">Holding area for transfers — visible to all admin levels</div>
          </div>
        )}
      </div>

      <Dialog open={posImportOpen} onOpenChange={(o) => !o && setPosImportOpen(false)}>
        <DialogContent className="bg-slate-900 border-slate-800 text-slate-100" data-testid="position-import-dialog">
          <DialogHeader><DialogTitle className="font-heading">Import positions</DialogTitle></DialogHeader>
          <p className="text-xs text-slate-500 mb-2">One per line: <span className="font-mono-num">Name, Level</span> (whole number; higher = more senior). Duplicate names are reported as errors and can be downloaded as a file.</p>
          <div className="flex gap-4 mb-2">
            <label className="flex items-center gap-2 text-sm text-slate-300">
              <input type="radio" name="pos-import-mode" className="accent-blue-600" checked={posImportMode === "append"} onChange={() => setPosImportMode("append")} data-testid="position-import-mode-append" />
              Append to existing
            </label>
            <label className="flex items-center gap-2 text-sm text-slate-300">
              <input type="radio" name="pos-import-mode" className="accent-blue-600" checked={posImportMode === "replace"} onChange={() => setPosImportMode("replace")} data-testid="position-import-mode-replace" />
              Replace existing
            </label>
          </div>
          <textarea value={posImportText} onChange={(e) => setPosImportText(e.target.value)} rows={6} data-testid="position-import-text"
            placeholder={"MajGen, 19\nBRIG, 18\nCOL, 17\n…\nCONT, 0"}
            className="w-full px-3 py-2.5 rounded-md bg-slate-950 border border-slate-800 text-sm font-mono-num" />
          <button onClick={runPosImport} disabled={posBusy} data-testid="position-import-btn"
            className="mt-4 w-full py-2.5 rounded-md bg-blue-600 hover:bg-blue-500 text-white text-sm font-medium disabled:opacity-50">
            {posBusy ? "Importing…" : "Import"}
          </button>
        </DialogContent>
      </Dialog>
    </div>
  );
}
